import React, { useState, useEffect, useRef, useCallback, useMemo, type RefObject } from "react"
import { SkeletonGrid } from "@/components/ui/skeleton-grid"
import type { PortfolioItem } from "@/types/models"
import { markDiscoverLoadPhase, summarizeDiscoverLoad } from "@/lib/discoverPerf"

/** Matches ITEMS_PER_LOAD in useQueryTattoos — caps skeleton gate before idle freeze. */
const SKELETON_IMAGE_GATE_BATCH = 40

interface BaseMasonryGridProps {
  items: PortfolioItem[]
  loading: boolean
  loadingMore: boolean
  hasMore: boolean
  error?: unknown
  loadMoreItems: () => Promise<void>
  renderItem: (item: PortfolioItem, artist?: any, editMode?: boolean, onDelete?: (itemId: string) => void) => React.ReactNode
  artists?: any[]
  artistsLoading?: boolean
  showSavedOnly?: boolean
  savesLoading?: boolean
  initialLoadComplete?: boolean
  reloadOnScroll?: boolean
  /** When set, infinite scroll uses this element instead of the window (nested grids / modals). */
  scrollRootRef?: RefObject<HTMLElement | null>
  debug?: boolean
  debugSkeleton?: boolean
  editMode?: boolean
  onDelete?: (itemId: string) => void
  // When true (cache hit), skip the skeleton entirely and show content immediately
  skipInitialSkeleton?: boolean
  /** When true, horizontal gap between columns matches vertical gap (both use gap-2). */
  compactUniformMasonryGap?: boolean
  /** Fired when the initial skeleton overlay is on-screen (including fade-out); false when dismissed or on unmount. */
  onSkeletonOverlayActiveChange?: (active: boolean) => void
}

interface Column {
  id: number
  items: PortfolioItem[]
  height: number
}

export function BaseMasonryGrid({ 
  items,
  loading,
  loadingMore,
  hasMore,
  error,
  loadMoreItems,
  renderItem,
  artists = [],
  artistsLoading = false,
  showSavedOnly = false,
  savesLoading = false,
  initialLoadComplete = false,
  reloadOnScroll = true,
  scrollRootRef,
  debug = false,
  debugSkeleton = false,
  editMode = false,
  onDelete,
  skipInitialSkeleton = false,
  compactUniformMasonryGap = false,
  onSkeletonOverlayActiveChange,
}: BaseMasonryGridProps) {
  const [columns, setColumns] = useState<Column[]>([{ id: 0, items: [], height: 0 }, { id: 1, items: [], height: 0 }])
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [imageReadyCount, setImageReadyCount] = useState(0)
  // 'showing' → skeleton in normal flow, grid hidden
  // 'fading'  → skeleton overlaid + fading out, grid revealed
  // 'hidden'  → skeleton removed from DOM
  // When returning from navigation with cached data, skip straight to 'hidden'
  const [skeletonPhase, setSkeletonPhase] = useState<'showing' | 'fading' | 'hidden'>(skipInitialSkeleton ? 'hidden' : 'showing')
  // Set to true after the skeleton is dismissed for the first time.
  // Prevents incremental loads (infinite scroll) from re-triggering the skeleton
  // when new images haven't loaded yet — only a key remount resets this.
  const hasRevealedRef = useRef(skipInitialSkeleton)
  const containerRef = useRef<HTMLDivElement>(null)
  const lastLoadTime = useRef(0)
  const previousItemCount = useRef(0)
  /** After first idle frame, freeze count so infinite scroll can't raise the image gate forever. */
  const skeletonRevealTargetRef = useRef<number | null>(null)
  const skeletonSummaryLoggedRef = useRef(false)

  // Reset ready count when items clear (new artist/filter)
  useEffect(() => {
    if (items.length === 0) {
      setImageReadyCount(0)
    }
  }, [items.length])

  // Count each image that has fired its onLoad event
  const handleImageVisible = useCallback((_itemId: string) => {
    setImageReadyCount((prev) => prev + 1)
  }, [])

  // Stable per-item callbacks so card memo() bails out when imageReadyCount changes.
  // Without this, every imageReadyCount increment re-creates all closures → cards
  // re-render → useImageLoadAndVisible re-runs — breaking the onLoad tracking chain.
  const itemIds = useMemo(() => items.map(i => i.id).join(','), [items])
  const itemCallbacks = useMemo(() => {
    const map = new Map<string, () => void>()
    items.forEach(item => {
      map.set(item.id, () => handleImageVisible(item.id))
    })
    return map
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemIds, handleImageVisible])
  
  // Calculate item height for layout
  const calculateItemHeight = useCallback((item: PortfolioItem) => {
    const aspectRatio = item.width && item.height ? item.height / item.width : 1
    const fixedWidth = 200
    const imageHeight = fixedWidth * aspectRatio
    const cardContentHeight = 80
    return imageHeight + cardContentHeight + 8 // 8px for gap
  }, [])

  // Incremental masonry layout - only add new items without redistributing
  const updateColumnsIncrementally = useCallback((items: PortfolioItem[]) => {
    setColumns(prevColumns => {
      // If this is a fresh load (filters changed), reset everything
      if (items.length < previousItemCount.current) {
        previousItemCount.current = 0
        const newColumns: Column[] = [
          { id: 0, items: [], height: 0 },
          { id: 1, items: [], height: 0 }
        ]
        
        // Distribute all items for fresh load
        items.forEach((item) => {
          const shortestColumn = newColumns.reduce((shortest, current) => 
            current.height < shortest.height ? current : shortest
          )
          shortestColumn.items.push(item)
          shortestColumn.height += calculateItemHeight(item)
        })
        
        previousItemCount.current = items.length
        return newColumns
      }
      
      // For incremental loading, only add new items
      const newItems = items.slice(previousItemCount.current)
      if (newItems.length === 0) return prevColumns
      
      // Track existing IDs to prevent duplicates in columns
      const existingIds = new Set<string>()
      prevColumns.forEach(col => col.items.forEach(it => existingIds.add(it.id)))
      
      const updatedColumns = [...prevColumns]
      
      newItems.forEach((item) => {
        if (existingIds.has(item.id)) {
          // Skip if this item is already rendered in any column
          return
        }
        existingIds.add(item.id)
        // Find the column with the shortest height
        const shortestColumn = updatedColumns.reduce((shortest, current) => 
          current.height < shortest.height ? current : shortest
        )
        
        // Add item to the shortest column
        shortestColumn.items.push(item)
        shortestColumn.height += calculateItemHeight(item)
      })
      
      previousItemCount.current = items.length
      return updatedColumns
    })
  }, [calculateItemHeight])

  // Update columns when items change
  useEffect(() => {
    if (items.length === 0) {
      setColumns([{ id: 0, items: [], height: 0 }, { id: 1, items: [], height: 0 }])
      previousItemCount.current = 0
      setImageReadyCount(0)
      skeletonRevealTargetRef.current = null
      return
    }

    updateColumnsIncrementally(items)
  }, [items, updateColumnsIncrementally])

  useEffect(() => {
    if (items.length === 0) return
    if (!initialLoadComplete || loading || loadingMore) return
    if (skeletonRevealTargetRef.current !== null) return
    skeletonRevealTargetRef.current = items.length
  }, [items.length, initialLoadComplete, loading, loadingMore])

  // Infinite scroll with scroll-based detection - trigger at 80% of content
  useEffect(() => {
    if (!reloadOnScroll || !hasMore || loadingMore || isLoadingMore) return

    const handleScroll = () => {
      const now = Date.now()
      
      // Faster debounce: prevent loading more than once every 200ms
      if (now - lastLoadTime.current < 200) return

      const root = scrollRootRef?.current
      let scrollTop: number
      let viewport: number
      let scrollHeight: number
      if (root) {
        scrollTop = root.scrollTop
        viewport = root.clientHeight
        scrollHeight = root.scrollHeight
      } else {
        scrollTop = window.pageYOffset || document.documentElement.scrollTop
        viewport = window.innerHeight
        scrollHeight = document.documentElement.scrollHeight
      }
      
      if (scrollHeight <= 0) return
      const scrollPercentage = (scrollTop + viewport) / scrollHeight
      if (!Number.isFinite(scrollPercentage)) return

      if (root) {
        // In a short fixed-height panel, scrollPercentage is often >= 0.8 even at scrollTop 0,
        // which fires loadMore in a tight loop and balloons items.length past imageReadyCount.
        const contentBarelyScrolls = scrollHeight <= viewport * 1.12
        if (contentBarelyScrolls && scrollTop < 20) return
      }

      if (scrollPercentage >= 0.8) {
        lastLoadTime.current = now
        if (debug) {
          console.log("[BaseMasonryGrid] load more at 80% scroll", {
            scrollPercentage: Math.round(scrollPercentage * 100),
            nested: !!root,
          })
        }
        setIsLoadingMore(true)
        loadMoreItems().finally(() => setIsLoadingMore(false))
      }
    }

    let ticking = false
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll()
          ticking = false
        })
        ticking = true
      }
    }

    const root = scrollRootRef?.current
    if (root) {
      root.addEventListener('scroll', throttledHandleScroll, { passive: true })
      root.addEventListener('touchmove', throttledHandleScroll, { passive: true })
      return () => {
        root.removeEventListener('scroll', throttledHandleScroll)
        root.removeEventListener('touchmove', throttledHandleScroll)
      }
    }

    window.addEventListener('scroll', throttledHandleScroll, { passive: true })
    window.addEventListener('touchmove', throttledHandleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll)
      window.removeEventListener('touchmove', throttledHandleScroll)
    }
  }, [reloadOnScroll, hasMore, loadingMore, isLoadingMore, loadMoreItems, scrollRootRef, debug])

  // Dismiss skeleton after a stable batch of images — not after every appended row (infinite scroll).
  const revealImageTarget =
    skeletonRevealTargetRef.current !== null
      ? skeletonRevealTargetRef.current
      : Math.min(items.length, SKELETON_IMAGE_GATE_BATCH)

  const imagesReady = items.length === 0 || imageReadyCount >= revealImageTarget

  const shouldShowSkeleton = !hasRevealedRef.current && (
    loading ||
    artistsLoading ||
    (showSavedOnly && savesLoading) ||
    !initialLoadComplete ||
    !imagesReady
  )

  // Drive skeleton fade-out: showing → fading → hidden
  // Must be declared after shouldShowSkeleton
  useEffect(() => {
    if (shouldShowSkeleton) {
      setSkeletonPhase('showing')
      return
    }
    hasRevealedRef.current = true
    setSkeletonPhase('fading')
    if (!skeletonSummaryLoggedRef.current) {
      skeletonSummaryLoggedRef.current = true
      markDiscoverLoadPhase(
        'masonry_visible',
        {
          itemCount: items.length,
          imagesReady: imageReadyCount,
          imageGate: revealImageTarget,
          artistsLoading,
        },
        { alwaysLog: true }
      )
      summarizeDiscoverLoad({
        itemCount: items.length,
        imagesReady: imageReadyCount,
        imageGate: revealImageTarget,
      })
    }
    const timer = setTimeout(() => setSkeletonPhase('hidden'), 500)
    return () => clearTimeout(timer)
  }, [
    shouldShowSkeleton,
    items.length,
    imageReadyCount,
    revealImageTarget,
    artistsLoading,
  ])

  useEffect(() => {
    onSkeletonOverlayActiveChange?.(!error && skeletonPhase !== 'hidden')
  }, [error, skeletonPhase, onSkeletonOverlayActiveChange])

  useEffect(() => {
    const notify = onSkeletonOverlayActiveChange
    if (!notify) return
    return () => {
      notify(false)
    }
  }, [onSkeletonOverlayActiveChange])

  // Enhanced render function that injects a stable onImageLoad callback per item.
  // Using itemCallbacks (memoized by item IDs) ensures the prop reference is stable
  // across re-renders caused by imageReadyCount incrementing, so card memo() can bail out.
  // MUST be before any conditional returns to follow rules of hooks.
  const renderItemWithCallback = useCallback((item: PortfolioItem, artist?: any, editMode?: boolean, onDelete?: (itemId: string) => void) => {
    const element = renderItem(item, artist, editMode, onDelete) as React.ReactElement
    return React.cloneElement(element, {
      onImageLoad: itemCallbacks.get(item.id)
    } as any)
  }, [renderItem, itemCallbacks])

  if (error) return <p className="py-4 text-sm text-destructive">Error loading feed: {String(error)}</p>

  if (items.length === 0 && !loading && !shouldShowSkeleton) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="text-lg font-medium text-foreground mb-2">
          No results found
        </div>
        <div className="text-sm text-muted-foreground">
          Try adjusting your filters
        </div>
      </div>
    )
  }

  return (
    <div className="w-full relative" ref={containerRef}>
      {/* Grid content — always in normal flow so it provides the container height.
          Fades in simultaneously with skeleton fade-out for a smooth crossfade. */}
      <div style={skeletonPhase === 'showing' && !debugSkeleton ? { 
        opacity: 0, 
        pointerEvents: 'none',
        transition: 'opacity 250ms ease-out'
      } : {
        transition: 'opacity 250ms ease-out'
      }}>
        {/* Custom Masonry Layout with Equal Width */}
        <div className={`flex ${compactUniformMasonryGap ? 'gap-2' : 'gap-3'} max-w-full pb-12`}>
          {columns.map((column) => (
            <div key={column.id} className="flex-1 flex flex-col gap-2 min-w-0">
              {column.items.map((item) => {
                const artist = artists.find((a: any) => a.id === item.artistId)
                
                return (
                  <div 
                    key={item.id} 
                    className="w-full max-w-full overflow-visible animate-in fade-in-0 slide-in-from-bottom-2 duration-300"
                    style={{ 
                      animationDelay: `${Math.min(column.items.indexOf(item) * 50, 500)}ms`,
                      animationFillMode: 'backwards',
                      // Start animation during fade phase for simultaneous transition
                      animationPlayState: skeletonPhase === 'hidden' ? 'running' : 'paused',
                    }}
                  >
                    {renderItemWithCallback(item, artist, editMode, onDelete)}
                  </div>
                )
              })}
            </div>
          ))}
        </div>
        
        {loadingMore || isLoadingMore ? (
          <div className="flex justify-center items-center py-8">
            <div className="text-sm text-muted-foreground">
              Loading more items...
            </div>
          </div>
        ) : null}

        {debug && (
          <div style={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            background: 'rgba(0,0,0,0.75)',
            color: '#fff',
            padding: '4px 8px',
            borderRadius: 4,
            fontSize: 12,
            zIndex: 1000,
          }}>
            {`Grid items: ${items.length} (unique: ${new Set(items.map(i => i.id)).size})`}
          </div>
        )}
      </div>

      {/* Skeleton — always position:absolute so ONLY opacity changes during the fade.
          Changing position and opacity in the same render kills the CSS transition.
          The grid div above (always in normal flow) provides the container height. */}
      {skeletonPhase !== 'hidden' && (
        <div
          className="transition-opacity duration-500 ease-out pointer-events-none bg-background"
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 10,
            opacity: skeletonPhase === 'fading' ? 0 : (debugSkeleton ? 0.7 : 1),
          }}
        >
          <SkeletonGrid itemCount={6} />
        </div>
      )}

      {/* Debug readiness status panel */}
      {debugSkeleton && (
        <div className="fixed bottom-24 left-3 z-50 pointer-events-none bg-black/80 text-white text-[10px] font-mono rounded-lg px-2 py-1.5 space-y-0.5 leading-tight">
          <div className={loading ? 'text-red-400' : 'text-green-400'}>{loading ? '⏳' : '✓'} backend loading</div>
          <div className={!initialLoadComplete ? 'text-red-400' : 'text-green-400'}>{!initialLoadComplete ? '⏳' : '✓'} initialLoadComplete</div>
          <div className={artistsLoading ? 'text-red-400' : 'text-green-400'}>{artistsLoading ? '⏳' : '✓'} artists loaded</div>
          <div className={!imagesReady ? 'text-yellow-400' : 'text-green-400'}>{!imagesReady ? '⏳' : '✓'} images {imageReadyCount}/{revealImageTarget} (gate)</div>
          <div className={shouldShowSkeleton ? 'text-red-400 font-bold' : 'text-green-400 font-bold'}>{shouldShowSkeleton ? '🔴 SKELETON' : '🟢 READY'}</div>
        </div>
      )}
    </div>
  )
} 